import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFilters } from "@/components/FilterBar";
import { QueryPanel } from "@/components/States";
import { Card, Tag } from "@/components/ui";
import { useOverview, useTimeseries } from "@/lib/api/queries";
import { defaultInterval } from "@/lib/filters";
import {
  delta,
  formatBucket,
  formatDelta,
  formatLatency,
  formatNumber,
  formatPercent,
  formatUsd,
} from "@/lib/format";
import type { Overview as OverviewData } from "@/types/api";

function Kpi({
  label,
  value,
  change,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  change?: number | null;
  hint?: string;
  tone?: "neutral" | "negative";
}) {
  const rising = change !== null && change !== undefined && change > 0;
  return (
    <Card>
      <div className="flex flex-col gap-1.5">
        <span className="text-label-2 text-label-alternative">{label}</span>
        <span
          className={`text-headline-1 font-bold tabular-nums ${tone === "negative" ? "text-status-negative" : "text-label-strong"}`}
        >
          {value}
        </span>
        <div className="flex items-center gap-2">
          {change !== null && change !== undefined && (
            <Tag size="small" color={rising ? "cautionary" : "positive"}>
              이전 기간 대비 {formatDelta(change)}
            </Tag>
          )}
          {hint && <span className="text-caption-1 text-label-assistive tabular-nums">{hint}</span>}
        </div>
      </div>
    </Card>
  );
}

function CostTrend() {
  const [filters] = useFilters();
  const interval = defaultInterval(filters);
  const query = useTimeseries(filters, "cost", interval, null);

  return (
    <Card>
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-body-1 font-semibold text-label-strong">비용 추이</h2>
        <span className="text-caption-1 text-label-assistive">{interval === "hour" ? "시간별" : "일별"} · USD</span>
      </div>
      <QueryPanel query={query} isEmpty={(data) => data.points.length === 0}>
        {(data) => (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={data.points.map((point) => ({
                bucket: point.bucket,
                cost: point.values["(unset)"] ?? 0,
              }))}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid stroke="var(--line-normal-alternative)" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={(value: string) => formatBucket(value, interval)}
                stroke="var(--label-assistive)"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                minTickGap={24}
              />
              <YAxis
                stroke="var(--label-assistive)"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={64}
                tickFormatter={(value: number) => formatUsd(value)}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--background-elevated-normal)",
                  border: "1px solid var(--line-normal-alternative)",
                  borderRadius: "var(--radius-sm)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 12,
                }}
                labelFormatter={(value: string) => formatBucket(value, interval)}
                formatter={(value: number) => [formatUsd(value), "비용"]}
              />
              <Line
                type="monotone"
                dataKey="cost"
                stroke="var(--primary-normal)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </QueryPanel>
    </Card>
  );
}

function Cards({ data }: { data: OverviewData }) {
  const previous = data.previous ?? undefined;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Kpi
        label="총 호출수"
        value={formatNumber(data.total_calls)}
        change={delta(data.total_calls, previous?.total_calls)}
        hint={`평균 지연 ${formatLatency(data.avg_latency_ms)}`}
      />
      <Kpi
        label="총 토큰"
        value={formatNumber(data.total_tokens)}
        change={delta(data.total_tokens, previous?.total_tokens)}
        hint={`입력 ${formatNumber(data.prompt_tokens)} · 출력 ${formatNumber(data.completion_tokens)}`}
      />
      <Kpi
        label="총 비용"
        value={formatUsd(data.total_cost_usd)}
        change={delta(
          Number.parseFloat(data.total_cost_usd),
          previous ? Number.parseFloat(previous.total_cost_usd) : undefined
        )}
      />
      <Kpi
        label="에러율"
        value={formatPercent(data.error_rate)}
        tone={data.error_rate > 0.05 ? "negative" : "neutral"}
        hint={`실패 ${formatNumber(data.error_count)}건`}
      />
    </div>
  );
}

export function Overview() {
  const [filters] = useFilters();
  const query = useOverview(filters);

  return (
    <div className="flex flex-col gap-4">
      <QueryPanel query={query}>{(data) => <Cards data={data} />}</QueryPanel>
      <CostTrend />
    </div>
  );
}
